
import { HelpDeskSectionItem } from '../HelpdeskSectionItem';

import { ScrollView, View } from 'react-native';
import { SectionContent } from '../SectionContent';
import { SectionItem } from '../SectionItem';
import { SectionTitle } from '../SectionTitle';

import { observer } from 'mobx-react';

import Show from '../../Show/Show';
import Title from '../../Title/Title';
import { AppointmentCard } from '../AppointmentCard/AppointmentCard';
import { AssistanceStackParamList } from '../../../types/navigation';
import { BOTTOM_SPACING_SCROLLVIEW, SPACING } from '../../../config/designSystem/Spacing';
import { COLORS } from '../../../config/designSystem/Colors';
import { GET_REDO_SESSION } from '../../../state/queries/Session';
import { Link, router, useNavigation, useScrollToTop } from 'expo-router';
import { SMALL_MARGIN_VERTICAL_FN, WEB_HORIZONTAL_MARGIN_FN, isWeb } from '../../../config/designSystem/Layout';
import { SurveyType } from '../../../state/stores/Survey/surveyType.enum';
import { branchingKey, helpdeskKey, organizationAssistanceKey, sessionStackKey, yourNextAppointmentKey } from '../../../constants/routeKeys';
import { client } from '../../../config/apollo/client';
import { concatPath } from '../../../utils/links/concatPath';
import { useAvailableDimensions } from '../../../hooks/ui/useAvailableDimensions';
import { useEffect, useRef, useState } from 'react';
import { useIsSmallWeb } from '../../../hooks/ui/useIsSmallWeb';
import { useStores } from '../../../hooks/useStores';


type NewAssistanceProps = AssistanceStackParamList['Assistance']
export const NewAssistance: React.FunctionComponent<NewAssistanceProps> = observer(() => {
  const rootStore = useStores();
  const labels = rootStore?.language.translate.MOBILE.ASSISTANCE;
  const assistanceData = rootStore?.user?.company;
  const { height, width } = useAvailableDimensions();

  const navigation = useNavigation();

  const showScheduler  = !!rootStore?.user?.company?.hasAccessToScheduler;
  const {isSmallWeb} = useIsSmallWeb();

  const ref = useRef(null);
  useScrollToTop(ref);


  const [redoSessionId, setRedoSessionId] = useState<string>('');

  useEffect(() => {
    getRedoSession();
  }, []);

  const getRedoSession = async () => {
    const { data } = await client.query({ query: GET_REDO_SESSION });
    if (data && 'redoSession' in data) {
      setRedoSessionId(data?.redoSession?._id);
    }
  };

  const goToSession = () => {
    router.navigate({
      pathname: concatPath([sessionStackKey,branchingKey], 3) as string,
      params: {
        sessionId: redoSessionId,
        fromPath: encodeURIComponent(false),
        fromReckoning: encodeURIComponent(false),
      }});
  };


  // the flows are sessions: the one of the wanted type is launched
  const goToSessionOfType = async (type: 'matching' | 'instantHelp' | 'scheduling') => {
    const sessionId = await rootStore.session.fetchSessionIdOfType(type);
    if (!sessionId) {
      console.warn('No session of type', type);
      return;
    }
    router.navigate({
      pathname: concatPath([sessionStackKey,branchingKey], 3) as string,
      params: {
        sessionId,
        reckoningId: null,
        fromPath: encodeURIComponent(false),
        fromReckoning: encodeURIComponent(false),
        color: encodeURIComponent(COLORS.surface.contrast),
        secondaryColor: encodeURIComponent(COLORS.surface.accent)
      }});
  };




  const goToOrganizationKey = () => {
    router.navigate(concatPath([organizationAssistanceKey],2));
  };

  const goToHelpDesk = () => {
    router.navigate(concatPath([helpdeskKey],2));
  };

  const onPressInternalRessources = () => {
    if (!rootStore.user?.user?.onboardingDone && !rootStore.user?.user?.redoSessionDone && !!redoSessionId && rootStore?.user?.company?.withRedoSession) {
      goToSession();
    } else {
      goToOrganizationKey();
    }
  };

  const onPressWpoScheduling = () => {
    if (rootStore.user.wpoAppointments.isPending) {
      // user interacted too fast
      return;
    }
    // Users can now hold one appointment per service type. The "schedule a
    // session" CTA always starts the survey; the dedicated "Your Next
    // Appointment" section above already surfaces any existing bookings.
    // Duplicate-per-service is enforced server-side in the chooseService dynq.
    onPressTakeSurvey({surveyType: SurveyType.wpoScheduling});
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      rootStore?.psychologist.fetchPsychologists();
    });
    return unsubscribe;
  }, [navigation]);

  // fetch on focus
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    if (assistanceData?.hasAccessToScheduler) {
      const getWpoAppointments = async() => {
        await rootStore.user.wpoAppointments.fetch();
      };
      unsubscribe = navigation.addListener('focus', () => {
        getWpoAppointments();
      });
    }
    return () => {
      unsubscribe?.();
    };
  }, [assistanceData?.hasAccessToScheduler, navigation, rootStore.user.wpoAppointments]);

  // Navigate to matching survey or instant help survey
  const onPressTakeSurvey = ({
    firstTime = true,
    surveyType = SurveyType.matching as SurveyType,
  }) => {
    const sessionTypeOf: Partial<Record<SurveyType, 'matching' | 'instantHelp' | 'scheduling'>> = {
      [SurveyType.matching]: 'matching',
      [SurveyType.wpoInstantHelp]: 'instantHelp',
      [SurveyType.wpoScheduling]: 'scheduling',
    };
    const type = sessionTypeOf[surveyType];
    if (type) {
      goToSessionOfType(type);
    } else {
      console.warn('No session for survey type', surveyType);
    }
  };

  const ratioInstantHelpImage = 2;
  return (
    <View style={{
      flex: 1,
      marginTop: !isWeb ? 30 : 0,
      marginHorizontal: 16,
      minWidth: 150
    }}>
      <Title
        title={labels?.NAVIGATION_TITLE}
        withBack={false}
        textColor={COLORS.content.primary}
      />
      <ScrollView
        contentContainerStyle={[isWeb && {
          flex: 1,
          marginTop: SMALL_MARGIN_VERTICAL_FN(height),
          marginHorizontal: WEB_HORIZONTAL_MARGIN_FN(width) / 3.5
        }, {
          paddingBottom: BOTTOM_SPACING_SCROLLVIEW,
        }]}
        ref={ref}
      >
        <Show when={showScheduler && rootStore.user.wpoAppointments.hasAny}>
          <SectionTitle>{labels?.YOUR_NEXT_APPT}</SectionTitle>
          {rootStore.user.wpoAppointments.sorted.map(appointment => (
            <Link key={appointment.appointmentId} href={yourNextAppointmentKey} asChild>
              <AppointmentCard
                appointmentModeLabelKey={appointment.appointmentModeLabel}
                counsellorName={appointment.counsellorName}
                locale={rootStore.language.language}
                date={appointment.dateFrom}
                timeZone={appointment.timeZone}
                style={{
                  marginBottom: SPACING['sp-16'],
                  borderColor: COLORS.border.accent,
                  borderWidth: 1,
                }}
              />
            </Link>
          ))}
          <View style={{ marginBottom: SPACING['sp-16'] }} />
        </Show>
        <Show when={assistanceData?.hasAccessToEvolunoAssistance || assistanceData?.hasAccessToWpoAssistance}>
          <SectionTitle>{labels?.THERAPY}</SectionTitle>
        </Show>
        <Show when={assistanceData?.hasAccessToEvolunoAssistance}>
          <SectionContent>
            <SectionItem
              onPress={() => onPressTakeSurvey({firstTime: true, surveyType: SurveyType.matching})}
              text={labels?.THERAPY_EVOLUNO_NETWORK}
              backgroundColor={COLORS.state.info.primary}
              textColor={COLORS.content['inverse-primary']}
            />
          </SectionContent>
        </Show>
        <Show when={assistanceData?.hasAccessToWpoAssistance}>
          <SectionContent flexDirection={isSmallWeb ? 'column' : 'row'}>
            <SectionItem
              onPress={() => onPressTakeSurvey({surveyType: SurveyType.wpoInstantHelp})}
              text={labels?.THERAPY_INSTANT_HELP}
              backgroundColor={COLORS.moonstone[800]}
              textColor={COLORS.content['inverse-primary']}
              nameImage={'assistance_instant_help'}
              positionImage={'top'}
              widthImage={108/ratioInstantHelpImage}
              heightImage={132/ratioInstantHelpImage}
            />
            <Show when={showScheduler}>
              <SectionItem
                onPress={onPressWpoScheduling}
                text={labels?.THERAPY_SCHEDULE_A_SESSION}
                backgroundColor={rootStore.user.wpoAppointments.isPending
                  ? COLORS.gray[500]
                  : COLORS.state.info.primary
                }
                textColor={COLORS.content['inverse-primary']}
                nameImage='assistance_schedule'
                positionImage='bottom'
                widthImage={279/2}
                heightImage={176/2}
              />
            </Show>
          </SectionContent>
        </Show>
        <Show when={rootStore?.user?.company?.hasAccessToOwnAssistance}>
          <SectionTitle>{labels?.YOUR_ORG}</SectionTitle>
          <SectionContent>
            <Show when={false}>
              <SectionItem
                onPress={() => {return;}}
                text={labels?.YOUR_ORG_FEEDBACK}
                backgroundColor={COLORS.surface['primary-dark']}
                textColor={COLORS.content['inverse-primary']}
              />
            </Show>
            <SectionItem
              onPress={onPressInternalRessources}
              text={labels?.YOUR_ORG_CONTACTS_AND_RESOURCES}
              backgroundColor={COLORS.brand[50]}
              textColor={COLORS.content['accent-dark']}
              nameImage={'assistance_contacts'}
              positionImage={'top'}
              widthImage={80/ratioInstantHelpImage}
              heightImage={244/ratioInstantHelpImage}
            />


          </SectionContent>
        </Show>
        <SectionTitle>{labels?.EVOLUNO_HELPDESK}</SectionTitle>
        <SectionContent>
          <HelpDeskSectionItem
            onPress={goToHelpDesk}
            text={labels?.EVOLUNO_HELPDESK_CONTENT}
            buttonText={labels?.CONTACT_US}
            backgroundColor={COLORS.state.info['inverse-primary']}
            textColor={COLORS.state.info.primary}
          />

        </SectionContent>


      </ScrollView >
    </View >
  );
});

